import { createFileRoute, Outlet, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { IoChevronBackOutline } from "react-icons/io5";
import { getCompetitionsRetrieveQueryOptions } from "@/api/generated/backendAPI";
import CompetitionImage from "@/components/ui/CompetitionImage";
import { useSidebarContent } from "@/providers/useSidebar";
import { CompetitionDetailSidebar } from "@/components/layout/CompetitionDetailSidebar";
import { navListBaseStyle } from "@/components/layout/competitionNavStyles";
import { getCompetitionStatus } from "@/lib/competitionStatus";
import MyTeamCard from "@/components/team/MyTeamCard";
import DistributionItemsCard from "@/components/team/DistributionItemsCard";

const navListStyle = `${navListBaseStyle} data-[status='active']:border-subcolor data-[status='active']:text-darkcolor`;

const statusBadge = {
  upcoming: <span className="bg-graycolor text-white text-[13px] font-semibold px-4 rounded-full">受付前</span>,
  active:   <span className="bg-badgeblue text-white text-[13px] font-semibold px-4 rounded-full">受付中</span>,
  ended:    <span className="bg-graycolor text-white text-[13px] font-semibold px-4 rounded-full">終了</span>,
};

export const Route = createFileRoute("/_layout/_auth/competitions/$licenseId")({
  loader: ({ context: { queryClient }, params }) => {
    return queryClient.ensureQueryData(getCompetitionsRetrieveQueryOptions(params.licenseId));
  },
  component: CompetitionDetail,
});

function CompetitionDetail() {
  const { licenseId } = Route.useParams();
  const { data: competitionData } = useSuspenseQuery(getCompetitionsRetrieveQueryOptions(licenseId));
  useSidebarContent(<CompetitionDetailSidebar licenseId={licenseId} />, [licenseId]);

  const endDatetime = new Date(competitionData.endDatetime);
  const status = getCompetitionStatus(competitionData.startDatetime, competitionData.endDatetime);

  return (
    <div className="w-full">
      <Link to="/competitions" className="flex items-center gap-1 text-sm text-navcolor hover:opacity-70 mb-4">
        <IoChevronBackOutline size={16} /><span>一覧に戻る</span>
      </Link>
      <div>
        <div className="mb-8">
          <CompetitionImage
            licenseId={licenseId}
            imageType="main"
            showNoImage={false}
            className="w-full h-auto max-h-[350px] object-cover object-center rounded-md"
          />
        </div>
        <div className="flex items-center gap-4 leading-[180%]">
          {statusBadge[status]}
          <span className="text-[15px] font-semibold text-navcolor">
            投稿締切日：{endDatetime.toLocaleDateString()}&nbsp;&nbsp;&nbsp;&nbsp;投稿数：{competitionData.submissionCount}
          </span>
        </div>
        <h1 className="text-[32px] font-bold mt-9">{competitionData.name}</h1>
        {competitionData.myTeam && (
          <MyTeamCard
            team={competitionData.myTeam}
            startDatetime={competitionData.startDatetime}
          />
        )}
        <DistributionItemsCard licenseId={licenseId} />
      </div>
      <div className="mt-4">
        <ul className="flex">
          <li>
            <Link to="/competitions/$licenseId/info" params={{ licenseId }} className={navListStyle}>
              概要
            </Link>
          </li>
          <li>
            <Link to="/competitions/$licenseId/data" params={{ licenseId }} className={navListStyle}>
              関連データ
            </Link>
          </li>
          <li>
            <Link to="/competitions/$licenseId/score" params={{ licenseId }} className={navListStyle}>
              リーダーボード
            </Link>
          </li>
          <li>
            <Link to="/competitions/$licenseId/submission" params={{ licenseId }} className={navListStyle}>
              投稿
            </Link>
          </li>
        </ul>
      </div>
      <div className="my-4 p-4 bg-white rounded-md shadow-xs w-full h-auto">
        <Outlet />
      </div>
    </div>
  );
}
